import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Public pages
import PublicWebsite from './components/Public/PublicWebsite';
import Disclaimer from './components/Pages/Disclaimer';
import PrivacyPolicy from './components/Pages/PrivacyPolicy';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import VerifyEmail from './components/Auth/VerifyEmail';
import AdminLogin from './components/Admin/AdminLogin';

// Protected routes
import UserRoutes from './routes/UserRoutes';
import AdminRoutes from './routes/AdminRoutes';
import ProtectedRoute from './components/Auth/ProtectedRoute';

import './App.css';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseKey || supabaseUrl === 'YOUR_SUPABASE_URL') {
  console.error('❌ Missing Supabase credentials!');
  console.error('Please follow these steps:');
  console.error('1. Copy .env.example to .env');
  console.error('2. Add your Supabase URL and Anon Key from https://supabase.com/dashboard');
  console.error('3. Restart the development server');
}

export const supabase = supabaseUrl && supabaseKey
  ? createClient(supabaseUrl, supabaseKey)
  : null;

// API Base URL
export const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

function App() {
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializedUsers = useRef(new Set());

  useEffect(() => {
    // Check if Supabase is configured
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session) {
        // Initialize user only if not already done
        if (!initializedUsers.current.has(session.user.id)) {
          try {
            const response = await fetch(`${API_BASE}/initialize-user`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              }
            });
            if (response.ok) {
              const data = await response.json();
              console.log('✅ User initialized:', data.profile?.username, 'Wallet:', data.wallet?.balance_rupees);
            }
            initializedUsers.current.add(session.user.id);
          } catch (error) {
            console.error('Failed to initialize user:', error);
          }
        }

        checkUserRole(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session) {
        // Initialize user only if not already done
        if (!initializedUsers.current.has(session.user.id)) {
          try {
            const response = await fetch(`${API_BASE}/initialize-user`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              }
            });
            if (response.ok) {
              const data = await response.json();
              console.log('✅ User initialized:', data.profile?.username, 'Wallet: ₹' + data.wallet?.balance_rupees);
            }
            initializedUsers.current.add(session.user.id);
          } catch (error) {
            console.error('Failed to initialize user:', error);
            // Don't block login if initialization fails
          }
        }

        checkUserRole(session.user.id);
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUserRole = async (userId) => {
    try {
      // Get the session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setUserRole('user');
        return;
      }

      const token = session.access_token;

      // First check Supabase profile
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching role:', error);
        setUserRole('user'); // Default to user if error
        return;
      }

      const role = data?.role || 'user';
      console.log('User role from Supabase:', role);

      // If role is admin, verify with backend API
      if (role === 'admin') {
        try {
          // Try to access admin endpoint to verify admin status
          const response = await fetch(`${API_BASE}/admin/matches`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            console.log('Admin access verified via API');
            setUserRole('admin');
          } else {
            console.warn('Admin access denied by API, defaulting to user role');
            setUserRole('user');
          }
        } catch (apiError) {
          console.warn('Could not verify admin with backend:', apiError.message);
          // Still set as admin if Supabase says so, but log the warning
          setUserRole(role);
        }
      } else {
        setUserRole(role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole('user');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading ZeroPing...</p>
      </div>
    );
  }

  // Show configuration error if Supabase is not set up
  if (!supabase) {
    return (
      <div className="app-container">
        <div className="auth-container">
          <div className="auth-header">
            <h1 className="game-title">
              <span className="zero">ZERO</span>
              <span className="ping">PING</span>
            </h1>
            <p className="tagline">Battle Royale Tournament Platform</p>
          </div>

          <div className="card" style={{ maxWidth: '600px', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '1.8rem',
              marginBottom: '20px',
              color: 'var(--danger-color)',
              textAlign: 'center'
            }}>
              ⚠️ Configuration Required
            </h2>

            <div style={{ marginBottom: '20px', lineHeight: '1.8' }}>
              <p style={{ marginBottom: '15px' }}>
                <strong>Supabase credentials are missing!</strong> Please follow these steps:
              </p>

              <ol style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                <li style={{ marginBottom: '10px' }}>
                  Create a <code style={{
                    background: 'var(--accent-bg)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    color: 'var(--primary-color)'
                  }}>.env</code> file in the <code style={{
                    background: 'var(--accent-bg)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    color: 'var(--primary-color)'
                  }}>frontend/</code> directory
                </li>
                <li style={{ marginBottom: '10px' }}>
                  Go to <a
                    href="https://supabase.com/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary-color)' }}
                  >Supabase Dashboard</a> → Settings → API
                </li>
                <li style={{ marginBottom: '10px' }}>
                  Copy your Project URL and Anon Key
                </li>
                <li style={{ marginBottom: '10px' }}>
                  Add to <code style={{
                    background: 'var(--accent-bg)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    color: 'var(--primary-color)'
                  }}>.env</code> file:
                </li>
              </ol>

              <pre style={{
                background: 'var(--primary-bg)',
                padding: '15px',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '0.9rem',
                border: '1px solid var(--border-color)'
              }}>
                {`REACT_APP_SUPABASE_URL=https://xxxxx.supabase.co
REACT_APP_SUPABASE_ANON_KEY=your_anon_key_here
REACT_APP_API_BASE=http://localhost:4000/api`}
              </pre>

              <p style={{ marginTop: '20px', color: 'var(--warning-color)' }}>
                <strong>5.</strong> Restart the development server:
              </p>
              <pre style={{
                background: 'var(--primary-bg)',
                padding: '10px',
                borderRadius: '8px',
                marginTop: '10px',
                border: '1px solid var(--border-color)'
              }}>
                npm start
              </pre>
            </div>

            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: 'rgba(0, 240, 255, 0.1)',
              borderRadius: '8px',
              border: '1px solid var(--primary-color)'
            }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>
                📚 <strong>Need help?</strong> Read the <code>SETUP_GUIDE.md</code> file in the project root for detailed instructions.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app-container">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicWebsite />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/login"
            element={
              session ? (
                <Navigate to={userRole === 'admin' ? '/admin' : '/dashboard'} replace />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/register"
            element={
              session ? (
                <Navigate to={userRole === 'admin' ? '/admin' : '/dashboard'} replace />
              ) : (
                <Register />
              )
            }
          />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Admin Login Route (Independent) */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected User Routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute session={session} userRole={userRole} requiredRole="user">
                <UserRoutes session={session} />
              </ProtectedRoute>
            }
          />

          {/* Protected Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute session={session} userRole={userRole} requiredRole="admin">
                <AdminRoutes session={session} />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
